/**
 * Groups raw `Nationality` entities into one entry per constituent country.
 *
 * `listNationalities` returns one row per nationality *combination* a player
 * record carries — a dual national's row is a single entity named e.g.
 * `"Albania Belgium"`, not two rows. Rendered 1:1 into a dropdown, every
 * combination competes with the single-country entries a user is actually
 * scanning for (SE-8325).
 *
 * This groups those raw rows into one option per atomic country, and tracks
 * every underlying entity name that includes that country (its own row plus
 * every combination row it appears in) so a filter built from the grouped
 * selection still matches dual-national players — selecting "Albania" must
 * also match the player recorded as `"Albania Belgium"`.
 *
 * Splitting can't naively break on spaces — that would cut "United States"
 * into two countries. Instead each name is classified atomic-vs-combination
 * by a word-break search against the set of names already known to be
 * atomic, resolved shortest-name-first so multi-word atomics (built only
 * from names no shorter entry decomposes into) are established before any
 * longer combination tries to use them as building blocks. A name that
 * cannot be fully decomposed this way is left as one atomic "country" —
 * the same fallback backend-v2 documented for the equivalent server-side
 * split (SE-8473).
 * @module utils/nationalityCountryGroups
 */

/** A raw `Nationality` entity as returned by `listNationalities`. */
export interface NationalityNode {
  readonly id: string;
  readonly name: string;
}

/** One dropdown-scannable country, with every underlying entity it groups. */
export interface CountryNationalityGroup {
  /** id of the underlying entity whose name is exactly this country */
  readonly id: string;
  /** country display name */
  readonly label: string;
  /**
   * Every underlying `Nationality` entity name that includes this country —
   * its own standalone row (when one exists) plus every combination row
   * naming it. Sorted, deduplicated, and kept as the RAW entity name (not
   * whitespace-normalized) so a filter built from it exact-matches whatever
   * a backend actually has stored. Use this (not `label` alone) to build a
   * name-matching filter that still returns dual-national players.
   */
  readonly memberNames: readonly string[];
}

const WHITESPACE = /\s+/;
const WHITESPACE_GLOBAL = /\s+/g;

/**
 * Normalizes a raw name: trims and collapses internal whitespace. Used only
 * to key grouping/dedup — the raw name is what's kept in `memberNames` (see
 * `groupNationalitiesByCountry`), since that's what gets sent to a backend
 * exact-name filter and must match the value as actually stored.
 * @param name - Raw nationality entity name
 * @returns Trimmed name with internal whitespace collapsed to single spaces
 */
const normalize = (name: string): string =>
  name.trim().replace(WHITESPACE_GLOBAL, " ");

/**
 * Finds a decomposition of `tokens` into consecutive groups that each match
 * a member of `dictionary`, preferring the fewest segments (so a two-word
 * atomic dictionary entry is preferred over two one-word entries when both
 * are available).
 * @param tokens - Space-split words of the candidate name
 * @param dictionary - Names already established as atomic countries
 * @returns The shortest decomposition into dictionary entries, or `null`
 * when no full decomposition exists
 */
const wordBreak = (
  tokens: readonly string[],
  dictionary: ReadonlySet<string>
): readonly string[] | null => {
  // best[i] = shortest decomposition of tokens[0..i), or null if unreachable
  const best = tokens.reduce<readonly (readonly string[] | null)[]>(
    (acc, _token, index) => {
      const end = index + 1;
      const decomposition = acc.reduce<readonly string[] | null>(
        (bestForEnd, prefix, start) => {
          if (!prefix) return bestForEnd;
          const candidate = tokens.slice(start, end).join(" ");
          if (!dictionary.has(candidate)) return bestForEnd;
          const candidateDecomposition = [...prefix, candidate];
          return !bestForEnd ||
            candidateDecomposition.length < bestForEnd.length
            ? candidateDecomposition
            : bestForEnd;
        },
        null
      );
      return [...acc, decomposition];
    },
    [[]]
  );
  return best[tokens.length] ?? null;
};

/** Accumulator for classifyNames's fold over shortest-name-first order. */
interface ClassifyAcc {
  readonly atomicNames: ReadonlySet<string>;
  readonly decompositions: ReadonlyMap<string, readonly string[]>;
}

/**
 * Classifies every unique nationality name as atomic (a single country) or
 * a combination, resolving shortest names first so longer combinations can
 * only decompose into names already known to be atomic.
 * @param uniqueNames - Deduplicated, normalized nationality names
 * @returns Map from name to its ordered list of constituent atomic countries
 */
const classifyNames = (
  uniqueNames: readonly string[]
): ReadonlyMap<string, readonly string[]> => {
  const byWordCount = [...uniqueNames].sort((a, b) => {
    const wordDiff = a.split(WHITESPACE).length - b.split(WHITESPACE).length;
    return wordDiff !== 0 ? wordDiff : a.length - b.length;
  });

  const { decompositions } = byWordCount.reduce<ClassifyAcc>(
    (acc, name) => {
      const tokens = name.split(WHITESPACE);
      const decomposition =
        tokens.length > 1 ? wordBreak(tokens, acc.atomicNames) : null;

      if (decomposition && decomposition.length > 1) {
        return {
          atomicNames: acc.atomicNames,
          decompositions: new Map([
            ...acc.decompositions,
            [name, decomposition],
          ]),
        };
      }
      // Single word, or no multi-country decomposition found — atomic.
      return {
        atomicNames: new Set([...acc.atomicNames, name]),
        decompositions: new Map([...acc.decompositions, [name, [name]]]),
      };
    },
    { atomicNames: new Set(), decompositions: new Map() }
  );

  return decompositions;
};

/**
 * Groups raw `Nationality` entities into one option per constituent country.
 * @param nodes - Raw entities from `listNationalities`
 * @returns One group per atomic country, sorted alphabetically by label
 */
export const groupNationalitiesByCountry = (
  nodes: readonly NationalityNode[]
): readonly CountryNationalityGroup[] => {
  const normalized = nodes
    .map(node => ({
      id: node.id,
      rawName: node.name,
      name: normalize(node.name),
    }))
    .filter(node => node.name.length > 0);

  const uniqueNames = [...new Set(normalized.map(node => node.name))];
  const decompositions = classifyNames(uniqueNames);

  const idByAtomicName = normalized.reduce<ReadonlyMap<string, string>>(
    (acc, node) =>
      acc.has(node.name) ? acc : new Map([...acc, [node.name, node.id]]),
    new Map()
  );

  const memberNamesByCountry = normalized.reduce<
    ReadonlyMap<string, ReadonlySet<string>>
  >((acc, node) => {
    const countries = decompositions.get(node.name) ?? [node.name];
    return countries.reduce<ReadonlyMap<string, ReadonlySet<string>>>(
      (innerAcc, country) => {
        const members = innerAcc.get(country) ?? new Set<string>();
        return new Map([
          ...innerAcc,
          [country, new Set([...members, node.rawName])],
        ]);
      },
      acc
    );
  }, new Map());

  return [...memberNamesByCountry.entries()]
    .map(([country, memberNames]): CountryNationalityGroup | null => {
      const id = idByAtomicName.get(country);
      return id
        ? {
            id,
            label: country,
            memberNames: [...memberNames].sort((a, b) => a.localeCompare(b)),
          }
        : null;
    })
    .filter((group): group is CountryNationalityGroup => group !== null)
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Builds a lookup from each group's id to its member entity names, for
 * expanding a selected country back into every matching entity name when
 * building a network filter (so a selection still returns dual nationals).
 * @param groups - Country groups from `groupNationalitiesByCountry`
 * @returns Map from each group's id to its member entity names
 */
export const buildNationalityMemberNamesById = (
  groups: readonly CountryNationalityGroup[]
): Readonly<Record<string, readonly string[]>> =>
  Object.fromEntries(groups.map(group => [group.id, group.memberNames]));
